import React from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { getImage, fallbackImages } from "../../config/instagramImages";

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            About King Bistro
          </h1>
          <p className="text-xl text-amber-100 max-w-3xl mx-auto">
            Our story of passion, tradition, and culinary excellence
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded in 2010, King Bistro Grille began as a dream to create
                an exceptional dining experience that combines traditional
                culinary techniques with modern innovation. Our founder, Chef
                Michael King, envisioned a restaurant where every dish tells a
                story and every meal becomes a memorable experience.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                What started as a small family restaurant has grown into one of
                the most beloved dining destinations in the city. Our commitment
                to using only the finest ingredients, sourced from local
                suppliers and sustainable farms, has remained unchanged since
                our opening day.
              </p>
              <p className="text-lg text-gray-600">
                Today, King Bistro Grille continues to honor our founding
                principles while embracing new culinary trends and techniques.
                We invite you to be part of our ongoing story and experience the
                passion that goes into every dish we serve.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getImage(
                    "/images/instagram/kitchen.jpg",
                    fallbackImages.kitchen
                  )}
                  alt="Chef in kitchen"
                  className="w-full h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Passion
              </h3>
              <p className="text-gray-600">
                We pour our hearts into every dish, ensuring that our passion
                for food translates into exceptional dining experiences for our
                guests.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Quality
              </h3>
              <p className="text-gray-600">
                We never compromise on quality, from the ingredients we source
                to the service we provide to every guest who walks through our
                doors.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Community
              </h3>
              <p className="text-gray-600">
                We believe in building strong relationships with our local
                community, from our suppliers to our guests who become part of
                our extended family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The talented individuals who make King Bistro Grille special
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Chef Michael King",
                role: "Executive Chef & Founder",
                bio: "With over 20 years of culinary experience, Chef Michael brings his passion for traditional techniques and innovative flavors to every dish.",
                image: getImage(
                  "/images/instagram/chef-michael.jpg",
                  fallbackImages.chefMichael
                ),
              },
              {
                name: "Sarah Chen",
                role: "Head Chef",
                bio: "Sarah's expertise in Asian fusion cuisine adds a unique dimension to our menu, creating dishes that are both familiar and surprising.",
                image: getImage(
                  "/images/instagram/sarah-chen.jpg",
                  fallbackImages.sarahChen
                ),
              },
              {
                name: "David Rodriguez",
                role: "Sous Chef",
                bio: "David's background in Mediterranean cuisine brings fresh perspectives and techniques to our kitchen, ensuring every dish is crafted with care.",
                image: getImage(
                  "/images/instagram/david-rodriguez.jpg",
                  fallbackImages.davidRodriguez
                ),
              },
            ].map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-amber-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="section-padding bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Recognition & Awards
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              "Best Fine Dining 2023",
              "Chef of the Year 2022",
              "Excellence in Service 2021",
              "Local Favorite 2023",
            ].map((award, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">{award}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
